import React, { useState, useEffect } from 'react';
import { supabase } from '../../../utils/supabaseClient';
import styles from './adminPageTeams.module.css';
import {User} from "@supabase/supabase-js";

interface AdminPageProps {
    user: User
}

interface Club {
    id: number;
    short_name: string;
    logo: string;
}

interface Team {
    team_id: number;
    team_club_id: number;
    team_club_short_name: string;
    team_club_logo: string;
    team_club_city: string;
    team_club_country: string;
}

interface Player {
    id: number;  // Это теперь id из таблицы persona2team
    first_name: string;
    last_name: string;
    number: number | null;
}

interface Season {
    id: number;
    name: string;
    start_date: string;
    end_date: string;
}

interface Tournament {
    id: number;
    name: string;
    short_name: string;
    fk_season: number;
}

const TeamManagement: React.FC<AdminPageProps> = () => {
    const [clubs, setClubs] = useState<Club[]>([]);
    const [selectedClub, setSelectedClub] = useState<number | null>(null);
    const [teams, setTeams] = useState<Team[]>([]);
    const [selectedTeam, setSelectedTeam] = useState<number | null>(null);
    const [players, setPlayers] = useState<Player[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [seasons, setSeasons] = useState<Season[]>([]);
    const [playerSeasons, setPlayerSeasons] = useState<{ [key: number]: number[] }>({});
    const [teamTournaments, setTeamTournaments] = useState<Tournament[]>([]);
    const [playerSeasonTournaments, setPlayerSeasonTournaments] = useState<{ [key: string]: number[] }>({});


    useEffect(() => {
        fetchClubs();
    }, []);

    useEffect(() => {
        fetchSeasons();
    }, []);

    useEffect(() => {
        if (selectedClub) {
            fetchTeams(selectedClub);
        }
    }, [selectedClub]);

    useEffect(() => {
        if (selectedTeam) {
            fetchPlayers(selectedTeam);
        }
    }, [selectedTeam]);

    useEffect(() => {
        if (selectedTeam) {
            fetchTeamTournaments(selectedTeam);
        }
    }, [selectedTeam]);

    const fetchTeamTournaments = async (teamId: number) => {
        try {
            const { data, error } = await supabase
                .from('team2tournament')
                .select(`
        fk_tournament,
        tournaments (
          id,
          name,
          short_name,
          fk_season
        )
      `)
                .eq('fk_team', teamId);

            if (error) throw error;

            console.log('Raw tournament data:', data); // Для отладки

            const tournaments: Tournament[] = data.map(item => {
                const tournament = Array.isArray(item.tournaments) ? item.tournaments[0] : item.tournaments;
                return {
                    id: tournament?.id,
                    name: tournament?.name,
                    short_name: tournament?.short_name,
                    fk_season: tournament?.fk_season
                };
            }).filter(tournament => tournament.id != null); // Убираем неполные данные

            console.log('Processed tournaments:', tournaments); // Для отладки

            setTeamTournaments(tournaments);
        } catch (error) {
            console.error('Error fetching team tournaments:', error);
            setError('Error fetching team tournaments');
        }
    };

    const fetchPlayerSeasonTournaments = async (teamId: number) => {
        try {
            const { data, error } = await supabase
                .from('persona2team2season2tournament')
                .select(`
                id,
                fk_tournament,
                persona2team2season!inner (
                    id,
                    fk_season,
                    fk_persona2team!inner (
                        id
                    )
                )
            `)
                .eq('persona2team2season.fk_persona2team.fk_team', teamId);

            if (error) throw error;

            console.log('Raw player season tournament data:', data);

            const tournamentsByPlayerSeason: { [key: string]: number[] } = {};
            data.forEach((item: any) => {
                const key = `${item.persona2team2season.fk_persona2team.id}-${item.persona2team2season.fk_season}`;
                if (!tournamentsByPlayerSeason[key]) {
                    tournamentsByPlayerSeason[key] = [];
                }
                tournamentsByPlayerSeason[key].push(item.fk_tournament);
            });

            console.log('Processed player season tournaments:', tournamentsByPlayerSeason);

            setPlayerSeasonTournaments(tournamentsByPlayerSeason);
        } catch (error) {
            console.error('Error fetching player season tournaments:', error);
            setError('Error fetching player season tournaments');
        }
    };

    const handleTournamentChange = async (playerId: number, seasonId: number, tournamentId: number, isChecked: boolean) => {
        try {
            // Сначала найдем соответствующую запись в persona2team2season
            const { data: personaTeamSeasonData, error: fetchError } = await supabase
                .from('persona2team2season')
                .select('id')
                .match({ fk_persona2team: playerId, fk_season: seasonId })
                .single();

            if (fetchError) throw fetchError;

            if (!personaTeamSeasonData) {
                throw new Error('No matching persona2team2season record found');
            }

            const personaTeamSeasonId = personaTeamSeasonData.id;

            if (isChecked) {
                // Добавляем турнир к игроку в сезоне
                const { error } = await supabase
                    .from('persona2team2season2tournament')
                    .insert({
                        fk_persona2team2season: personaTeamSeasonId,
                        fk_tournament: tournamentId
                    });

                if (error) throw error;
            } else {
                // Удаляем турнир у игрока в сезоне
                const { error } = await supabase
                    .from('persona2team2season2tournament')
                    .delete()
                    .match({
                        fk_persona2team2season: personaTeamSeasonId,
                        fk_tournament: tournamentId
                    });

                if (error) throw error;
            }

            // Обновляем данные после изменения
            await fetchPlayerSeasonTournaments(selectedTeam!);
        } catch (error) {
            console.error('Error updating player season tournament:', error);
            setError('Error updating player season tournament');
        }
    };


    const fetchSeasons = async () => {
        try {
            const { data, error } = await supabase
                .from('seasons')
                .select('*')
                .order('start_date', { ascending: false });

            if (error) throw error;
            setSeasons(data || []);
        } catch (error) {
            console.error('Error fetching seasons:', error);
            setError('Error fetching seasons');
        }
    };

    const fetchPlayerSeasons = async (teamId: number) => {
        try {
            const { data, error } = await supabase
                .from('persona2team2season')
                .select(`
                id, 
                fk_season,
                persona2team:fk_persona2team (
                    id,
                    fk_team
                )
            `)
                .eq('persona2team.fk_team', teamId);

            if (error) throw error;

            const seasonsByPlayer: { [key: number]: number[] } = {};
            data.forEach((item: any) => {
                if (item.persona2team && item.persona2team.id) {
                    if (!seasonsByPlayer[item.persona2team.id]) {
                        seasonsByPlayer[item.persona2team.id] = [];
                    }
                    seasonsByPlayer[item.persona2team.id].push(item.fk_season);
                }
            });

            setPlayerSeasons(seasonsByPlayer);
        } catch (error) {
            console.error('Error fetching player seasons:', error);
            setError('Error fetching player seasons');
        }
    };

    const fetchClubs = async () => {
        setIsLoading(true);
        try {
            const { data, error } = await supabase
                .from('clubs')
                .select('id, short_name, logo')
                .order('short_name');

            if (error) throw error;
            setClubs(data || []);
        } catch (error) {
            setError('Error fetching clubs');
            console.error('Error fetching clubs:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchTeams = async (clubId: number) => {
        setIsLoading(true);
        try {
            const { data, error } = await supabase
                .from('teams_short')
                .select('*')
                .eq('team_club_id', clubId);

            if (error) throw error;
            setTeams(data || []);
        } catch (error) {
            setError('Error fetching teams');
            console.error('Error fetching teams:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchPlayers = async (teamId: number) => {
        setIsLoading(true);
        try {
            const { data, error } = await supabase
                .from('persona2team')
                .select(`
                id,
                number,
                personas (
                    id,
                    first_name,
                    last_name
                )
            `)
                .eq('fk_team', teamId);

            if (error) throw error;
            const formattedPlayers = data.map(item => {
                const personas = Array.isArray(item.personas) ? item.personas[0] : item.personas;
                return {
                    id: item.id,  // Используем id из persona2team
                    first_name: personas.first_name,
                    last_name: personas.last_name,
                    number: item.number
                };
            }).sort((a, b) => {
                if (a.number !== null && b.number !== null) {
                    return a.number - b.number;
                }
                if (a.number === null && b.number !== null) return 1;
                if (a.number !== null && b.number === null) return -1;
                return a.last_name.localeCompare(b.last_name);
            });
            setPlayers(formattedPlayers);

            await fetchPlayerSeasons(teamId);
            await fetchPlayerSeasonTournaments(teamId);

        } catch (error) {
            setError('Error fetching players');
            console.error('Error fetching players:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSeasonChange = async (playerId: number, seasonId: number, isChecked: boolean) => {
        try {
            if (isChecked) {
                // Add season to player
                const { data, error } = await supabase
                    .from('persona2team2season')
                    .insert({ fk_persona2team: playerId, fk_season: seasonId });

                if (error) throw error;
            } else {
                // Remove season from player
                const { error } = await supabase
                    .from('persona2team2season')
                    .delete()
                    .match({ fk_persona2team: playerId, fk_season: seasonId });

                if (error) throw error;
            }

            // Refresh player seasons
            await fetchPlayerSeasons(selectedTeam!);
        } catch (error) {
            console.error('Error updating player season:', error);
            setError('Error updating player season');
        }
    };

    return (
        <div className={styles.teamManagement}>
            <h2>Team Management</h2>
            {error && <div className={styles.error}>{error}</div>}

            <div className={styles.clubSelection}>
                <h3>Select Club</h3>
                <select
                    value={selectedClub || ''}
                    onChange={(e) => setSelectedClub(Number(e.target.value))}
                    disabled={isLoading}
                >
                    <option value="">Select a club</option>
                    {clubs.map((club) => (
                        <option key={club.id} value={club.id}>
                            {club.short_name}
                        </option>
                    ))}
                </select>
            </div>

            {selectedClub && (
                <div className={styles.teamSelection}>
                    <h3>Select Team</h3>
                    <select
                        value={selectedTeam || ''}
                        onChange={(e) => setSelectedTeam(Number(e.target.value))}
                        disabled={isLoading}
                    >
                        <option value="">Select a team</option>
                        {teams.map((team) => (
                            <option key={team.team_id} value={team.team_id}>
                                {team.team_club_short_name}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            {selectedTeam && (
                <div className={styles.playerList}>
                    <h3>Team Players</h3>
                    {isLoading ? (
                        <p>Loading players...</p>
                    ) : (
                        <table>
                            <thead>
                            <tr>
                                <th>Number</th>
                                <th>Name</th>
                                {seasons.map(season => (
                                    <React.Fragment key={season.id}>
                                        <th>{season.name}</th>
                                        {teamTournaments
                                            .filter(tournament => tournament.fk_season === season.id)
                                            .map(tournament => (
                                                <th key={tournament.id}>{tournament.short_name}</th>
                                            ))
                                        }
                                    </React.Fragment>
                                ))}
                            </tr>
                            </thead>
                            <tbody>
                            {players.map((player) => (
                                <tr key={player.id}>
                                    <td>{player.number !== null ? player.number : '-'}</td>
                                    <td>{player.last_name} {player.first_name}</td>
                                    {seasons.map(season => {
                                        const playerSeasonId = playerSeasons[player.id]?.find(ps => ps === season.id);
                                        return (
                                            <React.Fragment key={season.id}>
                                                <td>
                                                    <input
                                                        type="checkbox"
                                                        checked={!!playerSeasonId}
                                                        onChange={(e) => handleSeasonChange(player.id, season.id, e.target.checked)}
                                                    />
                                                </td>
                                                {teamTournaments
                                                    .filter(tournament => tournament.fk_season === season.id)
                                                    .map(tournament => {
                                                        const key = `${player.id}-${season.id}`;
                                                        const isChecked = playerSeasonTournaments[key]?.includes(tournament.id);
                                                        return (
                                                            <td key={tournament.id}>
                                                                {playerSeasonId && (
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isChecked}
                                                                        onChange={(e) => handleTournamentChange(player.id, season.id, tournament.id, e.target.checked)}
                                                                        disabled={!playerSeasonId}
                                                                    />
                                                                )}
                                                            </td>
                                                        );
                                                    })
                                                }
                                            </React.Fragment>
                                        );
                                    })}
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
        </div>
    );
};

export default TeamManagement;